import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Lock } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { tierStyle } from "../lib/tiers";

/**
 * Encart affiché à la place de la composition et des mouvements d'un portefeuille verrouillé.
 * Accès strict par offre : un portefeuille Pro n'est pas inclus dans Essentiel.
 * - tier        : offre requise par le portefeuille
 * - currentTier : offre actuelle du lecteur ("free" si visiteur ou membre gratuit)
 * - loggedIn    : false → lien « Se connecter »
 */
export default function PortfolioPaywall({ tier, currentTier = "free", loggedIn = false }) {
  const { data: cfg } = useQuery({
    queryKey: ["config"],
    queryFn: async () => (await api.get("/config")).data,
  });
  const entry = cfg?.tiers?.[tier];
  const name = entry?.name || tier;
  const price = entry ? `${entry.price_eur.toString().replace(".", ",")} €/mois` : null;
  const currentName = cfg?.tiers?.[currentTier]?.name || currentTier;
  const isUpgrade = currentTier && currentTier !== "free";
  const s = tierStyle(tier); // couleur de l'offre requise

  return (
    <div
      className={`relative rounded-xl border bg-gradient-to-br to-transparent p-5 sm:p-8 md:p-10 overflow-hidden ${s.panel}`}
      data-testid="sideb-paywall"
    >
      <div className="absolute inset-0 grid-noise opacity-40 pointer-events-none" />
      <div className="relative grid md:grid-cols-[1fr_auto] gap-6 sm:gap-8 items-center">
        <div className="min-w-0">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] uppercase tracking-[0.2em] ${s.badge}`}>
            <Lock className="w-3 h-3" /> Portefeuille {name}
          </div>
          <h3 className="mt-4 font-display font-black text-2xl md:text-3xl">Découvrez ce qu'il y a dedans</h3>
          <p className="mt-2 text-zinc-400 max-w-xl">
            {isUpgrade
              ? `Votre abonnement ${currentName} ne comprend pas ce portefeuille. Passez à ${name} pour accéder à :`
              : `Avec l'offre ${name}, accédez à :`}
          </p>
          <ul className="mt-4 grid gap-2 text-sm text-zinc-300">
            {[
              "La composition détaillée et le poids de chaque actif",
              "L'historique de tous les dépôts, retraits et échanges",
              "Une notification à chaque nouveau mouvement",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2">
                <Check className={`w-4 h-4 mt-0.5 shrink-0 ${s.text}`} /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="min-w-0 flex flex-col items-center gap-3 md:min-w-[260px]">
          <Button asChild size="lg" className={`w-full h-11 ${s.cta}`} data-testid="sideb-paywall-cta">
            <Link to="/upgrade">
              {isUpgrade ? `Passer à ${name}` : `Débloquer avec ${name}`}
              {/* Téléphone : le prix passe sous le bouton pour que le texte tienne sur une ligne */}
              {price ? <span className="hidden sm:inline">{` · ${price}`}</span> : ""}
              <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </Button>
          {price && <div className="sm:hidden -mt-1 text-sm text-zinc-300">{price}</div>}
          <div className="flex flex-wrap justify-center gap-x-3 text-sm text-zinc-500">
            <Link to="/upgrade" className="hover:text-zinc-200 underline-offset-4 hover:underline">
              Voir toutes les offres
            </Link>
            {!loggedIn && (
              <>
                <span>·</span>
                <Link to="/login" className="hover:text-zinc-200 underline-offset-4 hover:underline">
                  Se connecter
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
